import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import { CheckBox, Flex, Small } from '@ubeya/shared-web/components';
import { required } from '@ubeya/shared/utils/form';
import styled from 'styled-components';
import { useHistory, useLocation } from 'react-router-dom';
import { useQueryParams } from '@ubeya/shared-web/hooks/useQueryParams';
import usePersistedState from '@ubeya/shared-web/hooks/usePersistedState';
import ErrorText from '@ubeya/shared-web/components/Input/ErrorText';
import AuthLayout from '../AuthLayout';
import { Box, PrimaryRoundButton, NumberFieldNoArrows, SecondaryRoundButton, ButtonsContainer } from '../styled';
import useMfa from '../../hooks/useMfa';

const RESEND_COOLDOWN_SECONDS = 30;

const ResendActions = styled(Flex)`
  width: 100%;
  gap: 16px;
  margin: 8px 0;
`;

const LinkButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  color: ${({ theme }) => theme.colors.primary};
  font-size: 13px;
  text-decoration: underline;
  cursor: pointer;

  &:disabled {
    color: ${({ theme }) => theme.colors.disabled};
    cursor: not-allowed;
    text-decoration: none;
  }
`;

const VerifyMfa = () => {
  const { t } = useContext(ConfigContext);
  const { push } = useHistory();
  const { state } = useLocation();
  const { mfaToken } = useQueryParams();
  const {
    verifyMfa,
    verifyMfaError,
    verifyMfaLoading,
    verifyReset,
    resendMfaCode,
    resendMfaError,
    resendMfaLoading,
    resendMfaSuccess,
    resendMfaReset
  } = useMfa();

  const [rememberMe, setRememberMe] = useState(true);
  const [_, setRememberMeToken] = usePersistedState('remember-me-token', '');
  const [cooldown, setCooldown] = useState(RESEND_COOLDOWN_SECONDS);
  const [lastChannel, setLastChannel] = useState(null);
  const intervalRef = useRef(null);
  const mfaPhone = state?.mfaPhone ? state.mfaPhone : null;

  useEffect(() => {
    if (cooldown <= 0) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }
    if (!intervalRef.current) {
      intervalRef.current = setInterval(() => {
        setCooldown((prev) => (prev > 0 ? prev - 1 : 0));
      }, 1000);
    }
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [cooldown]);

  const handleVerifyMfa = async ({ otp }) => {
    const data = await verifyMfa({ otp, mfaToken, rememberMe });

    if (data) {
      const { rememberMeToken } = data || {};
      if (rememberMeToken) setRememberMeToken(rememberMeToken);
    }
  };

  const handleResend = useCallback(
    async (channel) => {
      verifyReset();
      resendMfaReset();
      setLastChannel(channel);
      try {
        await resendMfaCode({ mfaToken, channel });
        setCooldown(RESEND_COOLDOWN_SECONDS);
      } catch (err) {
        const remaining = err?.response?.data?.params?.remainingCooldown;
        if (remaining) {
          setCooldown(remaining);
        }
      }
    },
    [mfaToken, resendMfaCode, resendMfaReset, verifyReset]
  );

  const handleRememberMeClick = useCallback(() => setRememberMe((prev) => !prev), []);

  const error = verifyMfaError?.response?.data?.message;
  const verifyAttemptsLeft = verifyMfaError?.response?.data?.params?.verifyAttemptsLeft;
  const resendCooldownFromServer = resendMfaError?.response?.data?.params?.remainingCooldown;
  const resendError = resendMfaError?.response?.data?.message;
  const resendButtonsDisabled = cooldown > 0 || resendMfaLoading;

  return (
    <AuthLayout>
      <Box onSubmit={handleVerifyMfa} title={t('verifyMfaTitle')} inside displayErrors={false}>
        {({ submitting, values }) => {
          if (error && error !== 'error__invalid_otp') {
            return (
              <>
                <Small>{error}</Small>
                <PrimaryRoundButton onClick={() => push(`/login/admin`)}>{t('backToLoginCaps')}</PrimaryRoundButton>
              </>
            );
          }
          return (
            <>
              <Small>{t('enterCodeMessage', { mfaPhone })}</Small>
              <NumberFieldNoArrows
                autoFocus
                name="otp"
                validate={required}
                type="number"
                step="any"
                title={`${t('OTP')}`}
                style={{ width: '100%' }}
              />
              <CheckBox name="rememberMe" value={rememberMe} onChange={handleRememberMeClick} label={t('rememberMe')} />
              {error === 'error__invalid_otp' && <ErrorText error={error} />}
              {!!verifyAttemptsLeft && (
                <ErrorText error={t('verifyCountAttemptsLeft', { count: verifyAttemptsLeft + 1 })} />
              )}
              <Small>{t('didntGetCode')}</Small>
              <ResendActions>
                <LinkButton type="button" disabled={resendButtonsDisabled} onClick={() => handleResend('sms')}>
                  {t('resendSms')}
                </LinkButton>
                <LinkButton type="button" disabled={resendButtonsDisabled} onClick={() => handleResend('call')}>
                  {t('callMeInstead')}
                </LinkButton>
                <LinkButton type="button" disabled={resendButtonsDisabled} onClick={() => handleResend('whatsapp')}>
                  {t('resendWhatsapp')}
                </LinkButton>
              </ResendActions>
              {cooldown > 0 && <Small>{t('resendCooldown', { seconds: cooldown })}</Small>}
              {resendMfaSuccess && cooldown > 0 && (
                <Small>{lastChannel === 'call' ? t('callOnTheWay') : t('resendCodeSuccess')}</Small>
              )}
              {resendError && !resendCooldownFromServer && <ErrorText error={resendError} />}
              <ButtonsContainer>
                <PrimaryRoundButton
                  disabled={submitting || verifyMfaLoading || !values?.otp}
                  isLoading={verifyMfaLoading}>
                  {!verifyMfaLoading && t('verifyCaps')}
                </PrimaryRoundButton>
                <SecondaryRoundButton onClick={() => push(`/login/admin`)}>{t('backToLoginCaps')}</SecondaryRoundButton>
              </ButtonsContainer>
            </>
          );
        }}
      </Box>
    </AuthLayout>
  );
};

export default VerifyMfa;
